import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GlassButton } from '../ui/GlassButton';
import { apiClient } from '../../services/api';
import './TosAcceptanceModal.css';

interface TosAcceptanceModalProps {
    onAccepted: () => void;
}

const TOS_VERSION = '1.0';

const TosAcceptanceModal: React.FC<TosAcceptanceModalProps> = ({ onAccepted }) => {
    const [agreed, setAgreed] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleAccept = async () => {
        if (!agreed) return;
        setIsSubmitting(true);
        setError('');
        try {
            await apiClient.post('/auth/accept-tos', { tos_version: TOS_VERSION });
            onAccepted();
        } catch (err: any) {
            setError('Failed to record acceptance. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="tos-modal-overlay">
            <div className="tos-modal">
                <div className="tos-modal-header">
                    <h2>Terms of Service Update</h2>
                    <p>We've updated our Terms of Service and Privacy Policy. Please review and accept to continue using AiMe.</p>
                </div>

                <div className="tos-modal-summary">
                    <h3>Key points</h3>
                    <ul>
                        <li>The platform must not be used to promote sexually explicit content, illicit drugs, firearms, or illegal activity</li>
                        <li>You are responsible for all AI-generated content you publish and all autonomous actions executed under your account</li>
                        <li>The platform is provided "as is" — we make no guarantees of marketing results</li>
                        <li>You must comply with applicable laws in every jurisdiction you operate in</li>
                        <li>Your data is not sold to third parties — see our Privacy Policy for full detail</li>
                    </ul>
                </div>

                <div className="tos-modal-links">
                    <Link to="/terms" target="_blank" rel="noopener noreferrer">
                        Read Terms of Service in full ↗
                    </Link>
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                        Read Privacy Policy in full ↗
                    </Link>
                </div>

                {error && <div className="tos-modal-error">{error}</div>}

                <label className="tos-modal-checkbox">
                    <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                    />
                    <span>
                        I have read and agree to the{' '}
                        <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                        {' '}and{' '}
                        <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                    </span>
                </label>

                <GlassButton
                    variant="primary"
                    size="lg"
                    onClick={handleAccept}
                    disabled={!agreed || isSubmitting}
                    loading={isSubmitting}
                    className="tos-modal-btn"
                >
                    Accept and Continue
                </GlassButton>
            </div>
        </div>
    );
};

export default TosAcceptanceModal;
